import BaseAPI from './base.api';
import {
  type LoadingPoint,
  type DumpingPoint,
  type SiteTravelTime,
  type MaterialGrade,
} from 'store/site-config.slice';

export interface SiteConfigPayload {
  loadingPoints: Array<Omit<LoadingPoint, 'isSelected'>>;
  dumpingPoints: Array<Omit<DumpingPoint, 'isSelected'>>;
  travelTimes: SiteTravelTime[];
  materialGrades?: MaterialGrade[];
}

export interface LatestApprovedDispatchPlan {
  planId: string;
  planName: string;
  customerCode: string;
  planData: {
    schedule: any[][];
  };
  inputData?: {
    targetBlend?: Record<string, number>;
    target_blend?: Record<string, number>;
    productionTarget?: number;
    production_target?: number;
    [key: string]: any;
  };
}

export interface DispatchModeResponse {
  dispatchMode: 'manual' | 'auto';
}

export default class FleetPlanningAPI extends BaseAPI {
  createDispatchPlan = async (payload: any) => {
    const response = await this.API.post(
      'fleet-planning/create-multi-dispatch-plan',
      payload
    );
    return response.data;
  };

  createDispatchPlanV2 = async (payload: any) => {
    const response = await this.API.post(
      'fleet-planning/v2/create-multi-dispatch-plan',
      payload
    );
    return response.data;
  };

  createDispatchPlanV3 = async (payload: any) => {
    const response = await this.API.post(
      'fleet-planning/v3/create-dispatch-plan',
      { ...payload, targetBlendTolerance: 0.05 }
    );
    return response.data;
  };

  getTripsStats = async (payload?: Record<string, unknown>) => {
    const response = await this.API.post(
      'fleet-planning/trips-stats',
      payload ?? {}
    );
    return response.data;
  };

  saveApprovedPlan = async (payload: any) => {
    const response = await this.API.post(
      'fleet-planning/save-approved-dispatch-plan',
      payload ?? {}
    );
    return response.data;
  };

  getApprovedDispatchPlanSummaryById = async (payload: Record<string, any>) => {
    const response = await this.API.post(
      'fleet-planning/dispatch-plan-summary-by-id',
      payload ?? {}
    );
    return response.data;
  };

  getApprovedDispatchPlanSummaryByDay = async (
    payload: Record<string, any>
  ) => {
    const response = await this.API.post(
      'fleet-planning/dispatch-plan-summary-by-day',
      payload ?? {}
    );
    return response.data;
  };

  getDispatchPlanHistory = async (payload: {
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await this.API.post(
      'fleet-planning/dispatch-plan-history',
      payload ?? {}
    );
    return response.data;
  };

  setActiveApprovedDispatchPlan = async (payload: Record<string, any>) => {
    const response = await this.API.post(
      'fleet-planning/set-active-approved-dispatch-plan',
      payload ?? {}
    );
    return response.data;
  };

  deleteDispatchPlanHistoryRecord = async (payload: Record<string, any>) => {
    const response = await this.API.post(
      'fleet-planning/delete-dispatch-plan-history-record',
      payload ?? {}
    );
    return response.data;
  };

  getSiteConfig = async (): Promise<SiteConfigPayload> => {
    const response = await this.API.get('fleet-planning/site-config');
    return response.data;
  };

  upsertSiteConfig = async (payload: SiteConfigPayload): Promise<void> => {
    await this.API.post('fleet-planning/upsert-site-config', payload);
  };

  getDispatchMode = async (
    customerId: string
  ): Promise<DispatchModeResponse> => {
    const response = await this.API.get('fleet-planning/dispatch-mode', {
      params: { customerId },
    });
    return response.data;
  };

  setDispatchMode = async (
    customerId: string,
    dispatchMode: 'manual' | 'auto'
  ): Promise<DispatchModeResponse> => {
    const response = await this.API.post('fleet-planning/dispatch-mode', {
      customerId,
      dispatchMode,
    });
    return response.data;
  };

  getLatestApprovedDispatchPlan = async (
    customerId: string
  ): Promise<LatestApprovedDispatchPlan> => {
    const response = await this.API.get(
      'fleet-planning/latest-approved-dispatch-plan',
      { params: { customerId } }
    );
    return response.data;
  };

  createDispatchPlanV4 = async (
    customerId: string,
    payload: {
      overtimeTolerance: number;
      excavators: Array<{
        bumperNumber: string;
        gradeId: string;
        capacity: number;
        lockedTrucks: string[];
        lockedDumps: string[];
        hardLock: boolean;
        nearbyGradeBlock: string[];
      }>;
      trucks: Array<{
        bumperNumber: string;
        startLocationId?: string;
      }>;
      saveAndDispatch: boolean;
      overrideDispatchPlan: boolean;
    }
  ) => {
    const response = await this.API.post(
      'fleet-planning/v4/create-dispatch-plan',
      payload,
      { params: { customerId } }
    );
    return response.data;
  };

  getMinimalFleetCount = async (payload: {
    customerId: string;
    grades: Array<{ gradeId: string; materialGrade: number }>;
    dumpingSites: Array<{ locationId: string; targetBlend: number }>;
    shiftNumber: number;
    targetDeliveredTons: number;
    targetBlendTolerance: number;
  }): Promise<any> => {
    const response = await this.API.post(
      'fleet-planning/v1/get-minimal-fleet-count',
      payload
    );
    return response.data;
  };
}
